import { stat, mkdir, readFile, writeFile, cp, rm } from 'fs/promises';
import path from 'path';
import { glob } from 'glob';

/**
 * Custom error class for file system operations
 */
export class FileSystemError extends Error {
  constructor(
    message: string,
    public readonly operation: string,
    public readonly path: string,
    public readonly cause?: Error
  ) {
    super(message);
    this.name = 'FileSystemError';
  }
}

/**
 * Validate that a path doesn't traverse outside a base directory
 * Prevents path traversal attacks (e.g., ../../../etc/passwd)
 */
export function validatePathSecurity(
  targetPath: string,
  baseDir: string
): void {
  const normalizedTarget = path.resolve(targetPath);
  const normalizedBase = path.resolve(baseDir);

  // Check if target is within base directory
  if (!normalizedTarget.startsWith(normalizedBase + path.sep) && normalizedTarget !== normalizedBase) {
    throw new FileSystemError(
      `Path traversal detected: "${targetPath}" is outside allowed directory "${baseDir}"`,
      'validatePathSecurity',
      targetPath
    );
  }
}

/**
 * Safe path join that validates the result stays within base directory
 */
export function safeJoinPath(baseDir: string, ...segments: string[]): string {
  const joined = path.join(baseDir, ...segments);
  validatePathSecurity(joined, baseDir);
  return joined;
}

/**
 * Check if a file exists
 */
export async function fileExists(filePath: string): Promise<boolean> {
  try {
    const s = await stat(filePath);
    return s.isFile();
  } catch {
    return false;
  }
}

/**
 * Check if a directory exists
 */
export async function directoryExists(dirPath: string): Promise<boolean> {
  try {
    const s = await stat(dirPath);
    return s.isDirectory();
  } catch {
    return false;
  }
}

/**
 * Ensure a directory exists (create if needed)
 */
export async function ensureDirectory(dirPath: string): Promise<void> {
  await mkdir(dirPath, { recursive: true });
}

/**
 * Read a JSON file with type safety
 */
export async function readJson<T>(filePath: string): Promise<T> {
  try {
    const content = await readFile(filePath, 'utf-8');
    try {
      return JSON.parse(content) as T;
    } catch (parseError) {
      throw new FileSystemError(
        `Invalid JSON in file: ${filePath}`,
        'readJson',
        filePath,
        parseError instanceof Error ? parseError : undefined
      );
    }
  } catch (error) {
    if (error instanceof FileSystemError) {
      throw error;
    }
    const err = error instanceof Error ? error : new Error(String(error));
    throw new FileSystemError(
      `Failed to read JSON file: ${filePath} - ${err.message}`,
      'readJson',
      filePath,
      err
    );
  }
}

/**
 * Write a JSON file
 */
export async function writeJson<T>(filePath: string, data: T): Promise<void> {
  try {
    await mkdir(path.dirname(filePath), { recursive: true });
    await writeFile(filePath, JSON.stringify(data, null, 2), 'utf-8');
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    throw new FileSystemError(
      `Failed to write JSON file: ${filePath} - ${err.message}`,
      'writeJson',
      filePath,
      err
    );
  }
}

/**
 * Read a text file
 */
export async function readText(filePath: string): Promise<string> {
  try {
    return await readFile(filePath, 'utf-8');
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    throw new FileSystemError(
      `Failed to read file: ${filePath} - ${err.message}`,
      'readText',
      filePath,
      err
    );
  }
}

/**
 * Write a text file
 */
export async function writeText(filePath: string, content: string): Promise<void> {
  try {
    await mkdir(path.dirname(filePath), { recursive: true });
    await writeFile(filePath, content, 'utf-8');
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    throw new FileSystemError(
      `Failed to write file: ${filePath} - ${err.message}`,
      'writeText',
      filePath,
      err
    );
  }
}

/**
 * Copy a file
 */
export async function copyFile(src: string, dest: string): Promise<void> {
  await mkdir(path.dirname(dest), { recursive: true });
  await cp(src, dest, { recursive: true });
}

/**
 * Remove a file
 */
export async function removeFile(filePath: string): Promise<void> {
  await rm(filePath, { force: true });
}

/**
 * Remove a directory
 */
export async function removeDirectory(dirPath: string): Promise<void> {
  await rm(dirPath, { recursive: true, force: true });
}

/**
 * Find files matching a glob pattern
 */
export async function findFiles(
  pattern: string,
  options: { cwd?: string; ignore?: string[] } = {}
): Promise<string[]> {
  const { cwd = process.cwd(), ignore = [] } = options;

  const files = await glob(pattern, {
    cwd,
    ignore: ['**/node_modules/**', '**/bin/**', '**/obj/**', ...ignore],
    absolute: true,
    nodir: true,
  });

  return files;
}

/**
 * Find directories matching a glob pattern
 */
export async function findDirectories(
  pattern: string,
  options: { cwd?: string; ignore?: string[] } = {}
): Promise<string[]> {
  const { cwd = process.cwd(), ignore = [] } = options;

  const dirs = await glob(pattern, {
    cwd,
    ignore: ['**/node_modules/**', '**/bin/**', '**/obj/**', ...ignore],
    absolute: true,
  });

  // Filter to only directories
  const results: string[] = [];
  for (const dir of dirs) {
    if (await directoryExists(dir)) {
      results.push(dir);
    }
  }

  return results;
}

/**
 * Get relative path from base
 */
export function relativePath(from: string, to: string): string {
  return path.relative(from, to).replace(/\\/g, '/');
}

/**
 * Normalize path separators
 */
export function normalizePath(filePath: string): string {
  return filePath.replace(/\\/g, '/');
}
